import {
  MoreHorizontal,
  CheckCircle2,
  Archive,
  Trash2,
  Eye,
  Bell,
} from "lucide-react";
import { cn } from "@/utils/Functions";
import { Checkbox } from "@/components/ui/Checkbox";
import {
  Menu,
  MenuTrigger,
  MenuPopup,
  MenuItem,
  MenuSeparator,
} from "@/components/ui/menu";
import { Avatar } from "@/components/ui/Avatar";
import type { InboxNotification } from "@/types/inbox.types";
import {
  getTypeIcon,
  getTypeColor,
  getActorAvatar,
  getActorInitials,
} from "../lib/utils";
import { useActorAvatarLookup } from "@/hooks/inbox/useActorAvatarLookup";

interface NotificationItemProps {
  notification: InboxNotification;
  selected: boolean;
  isDetailViewActive: boolean;
  onSelect: (id: string) => void;
  onViewDetail: (id: string) => void;
  onMarkRead: (id: string) => void;
  onArchive: (id: string) => void;
}

export function NotificationItem({
  notification,
  selected,
  isDetailViewActive,
  onSelect,
  onViewDetail,
  onMarkRead,
  onArchive,
}: NotificationItemProps) {
  const avatarLookup = useActorAvatarLookup();
  const Icon = getTypeIcon(notification.type);
  const typeColor = getTypeColor(notification.type);
  const isUnread = notification.status === "unread";
  const actorAvatar = getActorAvatar(notification.metadata.actor, avatarLookup);

  return (
    <div
      className={cn(
        "group relative flex items-start gap-3 p-4 transition-colors cursor-pointer",
        "hover:bg-muted/50",
        "rounded-2xl",
        isUnread && "bg-muted/23",
        selected && "bg-primary/5",
        isDetailViewActive && "bg-muted"
      )}
      onClick={() => onViewDetail(notification.id)}
    >
      {isUnread && (
        <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-primary" />
      )}

      <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={selected} onCheckedChange={() => onSelect(notification.id)} />
      </div>

      <div className={cn("shrink-0 p-2 rounded-md", typeColor)}>
        <Icon className="size-4" />
      </div>

      {notification.metadata.actor && (
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <Avatar
            src={actorAvatar}
            fallback={getActorInitials(notification.metadata.actor)}
            className="size-8"
            alt={notification.metadata.actor}
          />
        </div>
      )}

      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <span
            className={cn(
              "text-sm",
              isUnread ? "font-semibold text-foreground" : "font-medium text-muted-foreground"
            )}
          >
            {notification.title}
          </span>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {notification.relativeTime}
          </span>
        </div>

        <p className="text-sm text-muted-foreground line-clamp-1">{notification.description}</p>
      </div>

      <div
        className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <Menu>
          <MenuTrigger className="p-1.5 rounded-md hover:bg-muted transition-colors">
            <MoreHorizontal className="size-4 text-muted-foreground" />
          </MenuTrigger>
          <MenuPopup align="end" sideOffset={4}>
            <MenuItem onClick={() => onViewDetail(notification.id)}>
              <Eye className="size-4" />
              View details
            </MenuItem>
            <MenuItem onClick={() => onMarkRead(notification.id)}>
              {isUnread ? (
                <>
                  <CheckCircle2 className="size-4" />
                  Mark as read
                </>
              ) : (
                <>
                  <Bell className="size-4" />
                  Mark as unread
                </>
              )}
            </MenuItem>
            <MenuSeparator />
            <MenuItem onClick={() => onArchive(notification.id)}>
              <Archive className="size-4" />
              Archive
            </MenuItem>
            <MenuItem variant="destructive">
              <Trash2 className="size-4" />
              Delete
            </MenuItem>
          </MenuPopup>
        </Menu>
      </div>
    </div>
  );
}
